import { Button, Drawer, Select } from "@medusajs/ui"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { analytics } from "@/analytics/api"
import { useCan } from "@/lib/permissions"
import { DEFAULT_TILES, PICKABLE, useSaveLaunchDate, type CompareKey, type HomeData, type HomePrefs, type MetricKey, type RangeKey } from "./api"
import { ago, fmt, launchLabel, noBaselineHint, pct } from "./format"
import { Delta, Spark } from "./parts"

const RANGES: { value: RangeKey; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "mtd", label: "Month to date" },
  { value: "custom", label: "Custom…" },
]
const COMPARES: { value: CompareKey; label: string }[] = [
  { value: "prior", label: "vs Prior period" },
  { value: "same_day_last_week", label: "vs Same day last week" },
  { value: "none", label: "No comparison" },
]

/** "● 3 live": visitors active in the last 5 minutes (opens Live View). */
function LivePill() {
  const q = useQuery({ queryKey: ["analytics", "live-now"], queryFn: analytics.liveNow, refetchInterval: 15_000, refetchIntervalInBackground: false, retry: false })
  if (q.isError) return null
  const n = q.data?.visitors_right_now
  return (
    <Link
      to="/analytics/live"
      className="inline-flex h-8 items-center gap-2 rounded-[var(--k-r-sm)] bg-[var(--k-surface)] px-2.5 text-[13px] text-[var(--k-ink)] shadow-[var(--tb-shadow-btn)] hover:bg-[var(--k-sunken)]"
      title="Visitors on your store in the last 5 minutes"
    >
      <span className="relative flex h-2 w-2" aria-hidden>
        {n ? <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--k-positive)] opacity-60" /> : null}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${n ? "bg-[var(--k-positive)]" : "bg-[color:var(--k-dot-off-cool)]"}`} />
      </span>
      <span className="k-num">{n == null ? "–" : n.toLocaleString("en-US")}</span>
      <span>live</span>
    </Link>
  )
}

function SyncPill({ data }: { data?: HomeData }) {
  const [, tick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 30_000)
    return () => clearInterval(t)
  }, [])
  if (!data) return null
  const s = data.sync.settlements
  const lines = [
    `Orders, refunds, costs: live (as of ${new Date(data.generated_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })})`,
    s.last_ok_at ? `Authorize.net settlements: synced ${ago(s.last_ok_at)}` : "Authorize.net settlements: not synced yet",
    data.sync.ad_spend_at ? `Ad spend: synced ${ago(data.sync.ad_spend_at)}` : null,
  ].filter(Boolean)
  return (
    <span className="hidden h-8 items-center gap-1.5 whitespace-nowrap px-1 text-[13px] text-[var(--k-ink-muted)] sm:inline-flex" title={lines.join("\n")}>
      <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M16 10a6 6 0 0 1-10.4 4.1M4 10a6 6 0 0 1 10.4-4.1M14.5 3v3h-3M5.5 17v-3h3" />
      </svg>
      {s.last_ok_at ? `Synced ${ago(s.last_ok_at)}` : `Updated ${ago(data.generated_at)}`}
      {s.last_error ? <span className="text-[var(--k-alert)]" title={s.last_error}>· sync error</span> : null}
    </span>
  )
}

export function ControlBar({ prefs, save, data, storeName }: { prefs: HomePrefs; save: (p: Partial<HomePrefs>) => void; data?: HomeData; storeName: string }) {
  const [since, setSince] = useState(prefs.since ?? "")
  const [until, setUntil] = useState(prefs.until ?? "")
  useEffect(() => {
    setSince(prefs.since ?? "")
    setUntil(prefs.until ?? "")
  }, [prefs.since, prefs.until])
  const today = data?.window.range === "today" ? data.window.until : new Date().toISOString().slice(0, 10)
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="home-controls">
      {/* One store today; "All stores" waits for the next store on Keel (no made-up rollup). */}
      <Select value="default" onValueChange={() => undefined} size="small">
        <Select.Trigger className="w-auto min-w-[140px]" aria-label="Store">
          <Select.Value />
        </Select.Trigger>
        <Select.Content>
          <Select.Item value="default">{storeName}</Select.Item>
          <Select.Item value="all" disabled>
            All stores · when a second store is on Keel
          </Select.Item>
        </Select.Content>
      </Select>
      <Select value={prefs.range} onValueChange={(v) => save(v === "custom" ? { range: "custom", since: prefs.since ?? today, until: prefs.until ?? today } : { range: v as RangeKey })} size="small">
        <Select.Trigger className="w-auto min-w-[132px]" aria-label="Date range">
          <Select.Value />
        </Select.Trigger>
        <Select.Content>
          {RANGES.map((r) => (
            <Select.Item key={r.value} value={r.value}>
              {r.label}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
      {prefs.range === "custom" && (
        <span className="inline-flex items-center gap-1.5">
          <input type="date" aria-label="From" value={since} max={until || undefined} onChange={(e) => setSince(e.target.value)} onBlur={() => since && until && save({ since, until })} className="h-8 w-[140px] rounded-[var(--k-r-sm)] px-2 text-[13px]" />
          <span className="text-[var(--k-ink-muted)]">–</span>
          <input type="date" aria-label="To" value={until} min={since || undefined} onChange={(e) => setUntil(e.target.value)} onBlur={() => since && until && save({ since, until })} className="h-8 w-[140px] rounded-[var(--k-r-sm)] px-2 text-[13px]" />
        </span>
      )}
      <Select value={prefs.compare} onValueChange={(v) => save({ compare: v as CompareKey })} size="small">
        <Select.Trigger className="w-auto min-w-[176px]" aria-label="Compare to">
          <Select.Value />
        </Select.Trigger>
        <Select.Content>
          {COMPARES.map((c) => (
            <Select.Item key={c.value} value={c.value}>
              {c.label}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
      <LivePill />
      <SyncPill data={data} />
    </div>
  )
}

// ── KPI strip ────────────────────────────────────────────────────────────────

function tileValue(data: HomeData, key: MetricKey) {
  const def = data.definitions[key]
  const k = data.kpis[key]
  return { def, k, value: fmt(def, k.value), prev: k.previous == null ? null : fmt(def, k.previous) }
}

export function KpiStrip({ data, tiles, chart, onPick, onEdit }: { data: HomeData; tiles: MetricKey[]; chart: MetricKey; onPick: (k: MetricKey) => void; onEdit: () => void }) {
  const base = data.window.baseline_short
  return (
    <div>
      <div className="grid grid-cols-2 overflow-hidden rounded-[var(--k-r-md)] bg-[var(--k-sunken)] shadow-[inset_0_0_0_1px_var(--k-line)] md:grid-cols-3 xl:grid-cols-6" data-testid="kpi-strip">
        {tiles.map((key, i) => {
          const { def, k, value, prev } = tileValue(data, key)
          const brass = key === "contribution"
          const active = chart === key && def.series
          const isPct = def.format === "percent"
          return (
            <button
              key={key}
              type="button"
              onClick={() => def.series && onPick(key)}
              aria-pressed={active}
              title={`${def.label}: ${def.definition}`}
              data-testid={`kpi-${key}`}
              className={[
                "group relative flex min-w-0 flex-col items-start px-4 pb-3 pt-3.5 text-left transition-colors sm:px-5",
                // Hairlines between cells on every layout (2, 3 or 6 per row).
                "border-[var(--k-line)]",
                i % 2 === 1 ? "border-l md:border-l-0" : "",
                i % 3 !== 0 ? "md:border-l" : "",
                "xl:border-l xl:first:border-l-0",
                i >= 2 ? "border-t md:border-t-0" : "",
                i >= 3 ? "md:border-t xl:border-t-0" : "",
                brass ? "bg-[var(--k-brass-50)]" : active ? "bg-[var(--k-surface)]" : "hover:bg-[var(--k-surface)]",
                def.series ? "cursor-pointer" : "cursor-default",
              ].join(" ")}
            >
              {active && <span aria-hidden className={`absolute inset-x-0 top-0 h-[3px] ${brass ? "bg-[var(--k-brass)]" : "bg-[var(--k-blue)]"}`} />}
              <span className={`k-eyebrow truncate ${brass ? "text-[var(--k-brass-ink)]" : ""}`}>{def.short ?? def.label}</span>
              <span className={`k-num mt-1 text-[24px] font-medium leading-8 tracking-[-0.02em] sm:text-[26px] ${brass ? "text-[var(--k-brass-ink)]" : "text-[var(--k-ink)]"}`}>{value}</span>
              <span className="mt-0.5 flex min-h-[20px] flex-wrap items-baseline gap-x-1.5 text-[13px] leading-5">
                {brass && data.contribution_pct.value != null ? <span className="k-num text-[var(--k-brass-ink)]">{pct(data.contribution_pct.value, 1)} of net</span> : null}
                {data.window.compare !== "none" && (
                  <>
                    <Delta pct={isPct ? null : k.delta.pct} pts={isPct ? k.delta_pts : null} good={def.good} none={k.no_baseline} hint={k.no_baseline ? noBaselineHint(k.no_baseline, data.window) : undefined} />
                    {!brass && prev != null && !k.no_baseline && <span className="k-num truncate text-[var(--k-ink-muted)]">vs {prev}</span>}
                  </>
                )}
              </span>
              {base && !k.no_baseline && <span className="mt-0.5 w-full truncate text-[13px] leading-5 text-[var(--k-ink-muted)]">{base}</span>}
              <Spark values={k.spark} className="mt-2 h-7 w-full" color={brass ? "var(--k-brass)" : active ? "var(--k-blue)" : "var(--k-series-2)"} />
            </button>
          )
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <p className="text-[13px] leading-5 text-[var(--k-ink-muted)]">
          {data.window.baseline_status === "pre_launch" || data.window.baseline_status === "partial" ? (
            <>No baseline: {data.window.baseline_detail ?? "the comparison period"} {data.window.baseline_status === "pre_launch" ? "is" : "starts"} before the launch. </>
          ) : data.window.baseline ? (
            <>Deltas {data.window.baseline}{data.window.baseline_detail && data.window.grain === "day" ? ` (${data.window.baseline_detail})` : ""}. </>
          ) : (
            "No comparison. "
          )}
          Contribution = net sales − processor fees − COGS − shipping &amp; packaging. Click a tile to chart it. <LaunchDay data={data} />
        </p>
        <button type="button" onClick={onEdit} className="shrink-0 text-[13px] font-medium text-[var(--k-blue)] hover:underline" data-testid="edit-tiles">
          Edit tiles
        </button>
      </div>
    </div>
  )
}

/**
 * "Launched Nov 2": the day comparisons start from (backend lib/home/launch.ts). Worked out from the
 * first real order; staff with Analytics edit rights can set another day or go back to the automatic one.
 */
function LaunchDay({ data }: { data: HomeData }) {
  const can = useCan()
  const save = useSaveLaunchDate()
  const l = data.window.launch
  const [editing, setEditing] = useState(false)
  const [v, setV] = useState(l?.date ?? "")
  useEffect(() => setV(l?.date ?? ""), [l?.date])
  if (!l) return null
  const write = can("analytics:write")
  const label = launchLabel(l.date)
  const why = l.source === "setting" ? "set by hand" : l.source === "first_order" ? "the first storefront order" : null
  if (editing)
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5" data-testid="launch-editor">
        <input type="date" aria-label="Launch day" value={v} onChange={(e) => setV(e.target.value)} className="h-7 w-[140px] rounded-[var(--k-r-sm)] px-2 text-[13px]" />
        <button type="button" disabled={!v || save.isPending} className="font-medium text-[var(--k-blue)] hover:underline disabled:opacity-50" onClick={() => save.mutate(v, { onSuccess: () => setEditing(false) })}>
          Save
        </button>
        {l.source === "setting" && (
          <button type="button" disabled={save.isPending} className="text-[var(--k-blue)] hover:underline" onClick={() => save.mutate(null, { onSuccess: () => setEditing(false) })}>
            Use first order{l.first_order_date ? ` (${launchLabel(l.first_order_date)})` : ""}
          </button>
        )}
        <button type="button" className="hover:underline" onClick={() => setEditing(false)}>
          Cancel
        </button>
      </span>
    )
  return (
    <span data-testid="launch-day" title="Comparisons never reach back before this day: a period before it shows “No baseline” instead of a %.">
      {label ? `Launched ${label}${why ? ` (${why})` : ""}.` : "No launch day yet (no storefront order)."}
      {write && (
        <button type="button" className="ml-1 font-medium text-[var(--k-blue)] hover:underline" onClick={() => setEditing(true)} data-testid="launch-edit">
          Change
        </button>
      )}
    </span>
  )
}

/** "Edit tiles": six slots, each any metric Keel defines (saved on your user). */
export function TilePicker({ open, onClose, data, tiles, onSave }: { open: boolean; onClose: () => void; data?: HomeData; tiles: MetricKey[]; onSave: (t: MetricKey[]) => void }) {
  const [draft, setDraft] = useState(tiles)
  useEffect(() => setDraft(tiles), [tiles, open])
  const defs = data?.definitions
  const dupes = new Set(draft.filter((k, i) => draft.indexOf(k) !== i))
  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>KPI tiles</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-4 overflow-y-auto">
          <p className="text-[13px] leading-5 text-[var(--k-ink-muted)]">Pick the six numbers at the top of your Home. Saved for you on any device; everyone else keeps their own.</p>
          {draft.map((key, i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <label className="k-eyebrow" htmlFor={`tile-${i}`}>
                Tile {i + 1}
              </label>
              <Select value={key} onValueChange={(v) => setDraft((d) => d.map((x, j) => (j === i ? (v as MetricKey) : x)))}>
                <Select.Trigger id={`tile-${i}`} aria-invalid={dupes.has(key)}>
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  {PICKABLE.map((m) => (
                    <Select.Item key={m} value={m}>
                      {defs?.[m]?.label ?? m}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
              {defs?.[key] && <p className="text-[13px] leading-5 text-[var(--k-ink-muted)]">{defs[key].definition}</p>}
            </div>
          ))}
          {dupes.size > 0 && <p className="text-[13px] text-[var(--k-alert)]">Each tile needs a different metric.</p>}
        </Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" size="small" onClick={() => setDraft(DEFAULT_TILES)}>
            Reset to default
          </Button>
          <Button size="small" disabled={dupes.size > 0} onClick={() => (onSave(draft), onClose())}>
            Save tiles
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

